import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	DestroyRef,
	forwardRef,
	inject,
	input,
	OnInit,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
	ControlValueAccessor,
	FormControl,
	ReactiveFormsModule,
	NG_VALUE_ACCESSOR,
	FormArray,
	Validators,
} from '@angular/forms';
import { AlarisCardComponent } from '@campaign-portal/components-library';
import { DsFormLabelComponent } from '@alarislabs/angular-components/form-label';
import { DsSelectDropdownPresetComponent } from '@alarislabs/angular-components/dropdown';
import { TranslateModule } from '@ngx-translate/core';
import { DsButtonComponent } from '@alarislabs/angular-components/button';
import { DsSelectComponent } from '@alarislabs/angular-components/select';
import { DsIconSwapComponent } from '@alarislabs/angular-components/icon-swap';

@Component({
	selector: 'app-choice-form-array',
	templateUrl: './choice-form-array.component.html',
	styleUrl: './choice-form-array.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		ReactiveFormsModule,
		TranslateModule,
		AlarisCardComponent,
		DsFormLabelComponent,
		DsSelectDropdownPresetComponent,
		DsSelectComponent,
		DsButtonComponent,
		DsIconSwapComponent,
	],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => ChoiceFormArrayComponent),
			multi: true,
		},
	],
})
export class ChoiceFormArrayComponent<T extends { id: number | null; name: string }>
	implements ControlValueAccessor, OnInit
{
	private cdr = inject(ChangeDetectorRef);
	private readonly destroyRef = inject(DestroyRef);

	sources = input.required<string[], T[]>({
		transform: (sources: T[]): string[] => sources.map((item) => String(item.name)),
	});
	maxChoiceCount = input(3);

	public internalArray = new FormArray<FormControl<string>>([]);

	private onChange: (value: string[]) => void = () => {};
	onTouched: () => void = () => {};

	internalArraySignal = toSignal<string[]>(this.internalArray.valueChanges);

	ngOnInit(): void {
		this.internalArray.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((values) => {
			this.onChange(values);
		});
	}

	availableSources = computed(() => {
		return this.sources().filter((el) => !this.internalArraySignal()?.includes(el));
	});

	writeValue(value: string[] | null): void {
		this.internalArray.clear({ emitEvent: false });

		if (!value || (Array.isArray(value) && value.length === 0)) {
			this.internalArray.push(this.createControl(), { emitEvent: false });
		} else if (Array.isArray(value)) {
			value.forEach((item) => {
				this.internalArray.push(this.createControl(item), { emitEvent: false });
			});
		}

		this.cdr.markForCheck();
	}

	private createControl(value = ''): FormControl<string> {
		return new FormControl<string>(value, {
			nonNullable: true,
			validators: [Validators.required],
		});
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState?(isDisabled: boolean): void {
		isDisabled ? this.internalArray.disable() : this.internalArray.enable();
	}

	addItem(): void {
		this.internalArray.push(this.createControl());
		this.onTouched();
	}

	removeItem(index: number): void {
		this.internalArray.removeAt(index);
		this.onTouched();
	}
}
